import React, { createContext, useContext, ReactNode } from "react";
import { OrgaConfiguration } from "@/models";
import { useOrgaConfiguration } from "@/shared/hooks/useOrgaConfiguration";

import { getOrgaCompletionStatus } from "@/shared/hooks/useOrgaCompletion";

interface OrgaConfigurationContextType {
    configuration: OrgaConfiguration | null;
    loading: boolean;
    error: string | null;
    organizationName: string;
    organizationDescription: string;
    organizationLogo: string;
    primaryColor: string;
    secondaryColor: string;
    isActive: boolean;
    refreshConfiguration: (opts?: { silent?: boolean }) => void;
    // Ajout du statut de complétude
    completionStatus: ReturnType<typeof getOrgaCompletionStatus>;
}

const OrgaConfigurationContext = createContext<OrgaConfigurationContextType | undefined>(
    undefined
);

/**
 * Provider pour la configuration d'organisation
 * Permet de charger la configuration UNE SEULE FOIS pour toute la branche de composants
 */
export const OrgaConfigurationProvider = ({
    children,
    organizationId,
    once = false,
    initialConfiguration = null,
}: {
    children: ReactNode;
    organizationId?: string;
    once?: boolean;
    initialConfiguration?: OrgaConfiguration | null;
}) => {
    const config = useOrgaConfiguration(organizationId, { once, initialConfiguration });

    const completionStatus = React.useMemo(() => {
        return getOrgaCompletionStatus(config.configuration);
    }, [config.configuration]);

    return (
        <OrgaConfigurationContext.Provider value={{ ...config, completionStatus }}>
            {children}
        </OrgaConfigurationContext.Provider>
    );
};

/**
 * Hook pour accéder à la configuration d'organisation depuis le contexte
 */
export const useOrgaConfigurationContext = () => {
    const context = useContext(OrgaConfigurationContext);
    if (context === undefined) {
        throw new Error(
            "useOrgaConfigurationContext must be used within an OrgaConfigurationProvider"
        );
    }
    return context;
};
